import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Check } from 'lucide-react';
import { Product } from '../../types';
import { vendorApi } from '../../services/api';
import { extractApiError } from '../../utils/errorUtils';
import { formatCurrency } from '../../utils/currency';
import ErrorAlert from '../ErrorAlert';

interface AddToFairModalProps {
  product: Product;
  fairId: number;
  vendorId: number;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Modal for adding a product from master catalog to a specific fair
 * Allows setting fair-specific price, quantity, featured status, and pre-order availability
 */
export default function AddToFairModal({ product, fairId, vendorId, onClose, onSuccess }: AddToFairModalProps) {
  const [fairPrice, setFairPrice] = useState(product.price || '');
  const [quantity, setQuantity] = useState(0);
  const [isFeatured, setIsFeatured] = useState(false);
  const [allowPreorder, setAllowPreorder] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Auto-focus first input on mount
  useEffect(() => {
    const firstInput = dialogRef.current?.querySelector('input');
    firstInput?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await vendorApi.addProductToFairCatalog(vendorId, fairId, {
        product_id: product.id,
        fair_specific_price: fairPrice !== product.price ? fairPrice : undefined,
        quantity_available: quantity,
        is_featured: isFeatured,
        is_available_for_preorder: allowPreorder
      });
      onSuccess();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to add product to fair:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      role="presentation"
      tabIndex={-1}
      onClick={() => !loading && onClose()}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !loading) {
          onClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-to-fair-modal-title"
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="add-to-fair-modal-title" className="text-lg font-semibold text-gray-900 mb-4">
          Add "{product.name}" to Fair
        </h3>

        {error && (
          <ErrorAlert
            message={error}
            onDismiss={() => setError(null)}
            dismissible={!loading}
            className="mb-4"
          />
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fair Price (optional override)
            </label>
            <input
              type="text"
              value={fairPrice}
              onChange={(e) => {
                const value = e.target.value;
                // Allow empty, digits, and single decimal point with up to 2 decimal places
                if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
                  setFairPrice(value);
                }
              }}
              placeholder={`Base price: ${formatCurrency(product.price || '0')}`}
              pattern="^\d+(\.\d{0,2})?$"
              inputMode="decimal"
              disabled={loading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <p className="mt-1 text-xs text-gray-500">
              Leave empty to use base price
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quantity Available <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="0"
              max="100000"
              step="1"
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
              required
              disabled={loading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="featured"
              checked={isFeatured}
              onChange={(e) => setIsFeatured(e.target.checked)}
              disabled={loading}
              className="w-4 h-4 text-teal-600 rounded focus:ring-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <label htmlFor="featured" className="text-sm text-gray-700">
              Mark as featured product
            </label>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="preorder"
              checked={allowPreorder}
              onChange={(e) => setAllowPreorder(e.target.checked)}
              disabled={loading}
              className="w-4 h-4 text-teal-600 rounded focus:ring-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <label htmlFor="preorder" className="text-sm text-gray-700">
              Allow pre-orders
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Add to Fair
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
